import React, { useContext, useState } from 'react';
import { Box, Checkbox, FormControlLabel, TableBody, TableCell, TableRow } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { GameFilesUpload, noFilesTextForGameFilesUpload } from './GameFilesUpload';
import { UploadedFile } from '../../../../../../common/domain/upload';
import { UploadInstructions } from './UploadInstructions';
import TypeSelect from './TypeSelect';
import { StyledBodyText } from '../../../../../../common/Styleguide/Common/Text';
import { StyledButton } from '../../../../../../common/Styleguide/Common/Button';
import { StyledInput } from '../../../../../../common/Styleguide/Common/Input';
import GameDataSaveOptions from './GameDataSaveOptions';
import { NON_RELEASED_STATUSES } from '../UpdateSubmission/UpdateSubmission.types';
import { renderFileWarnings } from '../../helpers/warningHelpers';
import { SubmissionContext } from '../Context/SubmissionProvider';
import { renderHardWarning } from './utils';
import TextAlert from '../../TextAlert';
import { InputTable } from '../GameDetailsForm/GameDetailsForm';
import { COLORS } from '../../../../../../common/Styleguide/Common/colors';
import { isDefined } from '@crazygames/utils/isDefined';
import { DOCS_URL } from '@crazygames/game-types/src/submission/qa-results/qa-result.type';
import { GameLoaderType, UNITY_GAME_LOADERS, UNITY_LOADERS_CUSTOM_BUILD } from '@crazygames/game-types/src/game-build/loader.type';
import { BUILD_REPORT_FILE_NAME } from '@crazygames/game-types/src/game-build/custom-build.type';
import UnityCompressionWarnings from './UnityLoaderOptions/UnityCompressionWarnings';

/**
 * This component is displayed when you submit a new game, and when you want to submit a new file update (this one still do be done).
 * It renders:
 * - Game type select (HTML5, Unity, etc.)
 * - File upload, or iframe link input
 * - Game options (APS toggles, mobile friendly checkbox, iap checkbox)
 */
const UploadGameFilesForm: React.FC = () => {
  const {
    baseForm,
    attemptedToSave,
    baseFormProblems,
    submission,
    gameBuild,
    updateBaseForm,
    uploadingFilesSoftProblems,
    uploadingFilesHardProblems,
    onFilesToUploadSet,
    disableDetailsEditing,
    gameJamLocked,
  } = useContext(SubmissionContext);

  const { gameLoaderType, hasIAP, multiplayerOptions, isAndroidFriendly, isIOSFriendly, progressSaveType } = baseForm;

  const [instructionDialogOpen, setInstructionDialogOpen] = useState<boolean>(false);
  const [showUnityiOSWarning, setShowUnityiOSWarning] = useState<boolean>(false);
  const [showUseBuildScriptWarning, setShowUseBuildScriptWarning] = useState<boolean>(false);
  const [showArchiveNotSupportedError, setShowArchiveNotSupportedError] = useState<boolean>(false);

  const renderLoaderTypeSelect = () => {
    const { gameLoaderType } = baseForm;
    const typeError = attemptedToSave && baseFormProblems?.includes('GAME_LOADER_TYPE_MISSING');
    const excludedGameLoader: GameLoaderType[] = [];
    // exclude iframe loader for released submissions, as we don't want developers to change to iframe once the submission is released
    if (submission && gameBuild && gameBuild.loaderType !== 'iframe' && !NON_RELEASED_STATUSES.includes(submission.status)) {
      excludedGameLoader.push('iframe');
    }
    return (
      <>
        <TypeSelect
          error={typeError}
          gameLoaderType={gameLoaderType || undefined}
          onGameLoaderTypeChange={(gameLoaderType) => {
            updateBaseForm({ ...baseForm, gameLoaderType });
          }}
          disabled={gameJamLocked}
          isGameJam={!!baseForm.gameJamId}
          excludedGameLoaders={excludedGameLoader}
          typeError={typeError}
          showOtherEnginesInfo={!isDefined(submission)}
        />
      </>
    );
  };

  const renderIframeLinkInput = () => {
    const iframeLink = baseForm.iframeLink;
    const isHttp = !!iframeLink && iframeLink.startsWith('http://');
    const httpHelperText = isHttp
      ? 'Preview will not work for HTTP, if you support HTTPS please use that link instead.'
      : 'http(s)://example.com';
    const iframeError = (attemptedToSave && baseFormProblems?.includes('IFRAME_INVALID_URL')) || isHttp;
    return (
      <TableRow>
        <TableCell>
          <StyledBodyText variant="h3">IFrame link</StyledBodyText>
        </TableCell>
        <TableCell>
          <StyledInput
            required
            type="url"
            value={iframeLink ? iframeLink : ''}
            onChange={(e) => {
              updateBaseForm({ ...baseForm, iframeLink: e.currentTarget.value });
            }}
            error={iframeError}
          />
          <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="black10">
            {httpHelperText}
          </StyledBodyText>
        </TableCell>
      </TableRow>
    );
  };

  const renderGameFilesUpload = () => {
    const { files, gameLoaderType } = baseForm;
    const uploadMissingError =
      attemptedToSave && (baseFormProblems?.includes('HTML5_FILES_MISSING') || baseFormProblems?.includes('UNITY_FILES_MISSING'));
    const uploadingError = attemptedToSave && baseFormProblems?.includes('GAME_FILE_UPLOAD_IN_PROGRESS');
    const limitsExceededError =
      baseFormProblems?.includes('TOTAL_FILE_COUNT_EXCEEDED') ||
      baseFormProblems?.includes('TOTAL_FILE_SIZE_EXCEEDED') ||
      uploadingFilesHardProblems?.includes('TOTAL_FILE_COUNT_EXCEEDED') ||
      uploadingFilesHardProblems?.includes('TOTAL_FILE_SIZE_EXCEEDED');
    const initialFiles = files || [];

    return (
      <TableRow>
        <TableCell>
          <StyledBodyText variant="h3" sx={{ m: 0, display: 'flex', alignItems: 'center' }}>
            Upload files<span>&nbsp;*</span>
            <StyledButton onClick={() => setInstructionDialogOpen(!instructionDialogOpen)} height={22}>
              <HelpIcon />
            </StyledButton>
          </StyledBodyText>
        </TableCell>
        <TableCell>
          <GameFilesUpload
            initialFiles={initialFiles}
            error={uploadMissingError || uploadingError || limitsExceededError}
            gameLoaderType={gameLoaderType ?? undefined}
            onUploadsStarted={handleUploadStarted}
            onFilesModified={handleUploadCompleted}
            onFilesToUploadSet={onFilesToUploadSet}
          />
          <UnityCompressionWarnings gameLoaderType={gameLoaderType ?? null} files={files} />
          {uploadMissingError && renderHardWarning(`Please upload your files`)}
          {uploadMissingError && renderHardWarning(noFilesTextForGameFilesUpload(gameLoaderType))}
          {uploadingError && renderHardWarning(`Please wait until Game file upload is complete`)}
          {renderFileWarnings(uploadingFilesHardProblems || [], uploadingFilesSoftProblems || [])}
          {showUseBuildScriptWarning && (
            <TextAlert>
              We recommend using the{' '}
              <a href={`${DOCS_URL}/resources/unity-custom-build/`} target="_blank" rel="noreferrer">
                CrazyGames custom build
              </a>{' '}
              from our SDK for improved performance and reduced file size.
            </TextAlert>
          )}
          {showArchiveNotSupportedError && (
            <TextAlert color="error">
              Archive files are not supported, please drag and drop the files directly in the upload zone.
            </TextAlert>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const handleUploadStarted = () => {
    updateBaseForm({ ...baseForm, uploadInProgress: true });
  };

  const handleUploadCompleted = (files: UploadedFile[]) => {
    updateBaseForm({ ...baseForm, files, uploadInProgress: false });

    if (files.length > 0 && isDefined(baseForm.gameLoaderType)) {
      const hasBuildReport = files.some((f) => f.size > 0 && f['path']?.endsWith(BUILD_REPORT_FILE_NAME));
      setShowUseBuildScriptWarning(UNITY_LOADERS_CUSTOM_BUILD.includes(baseForm.gameLoaderType) && !hasBuildReport);
    }

    if (files.length === 1) {
      const fileName = files[0].path || '';
      const isArchive = fileName.endsWith('.zip') || fileName.endsWith('.rar') || fileName.endsWith('.7z') || fileName.endsWith('.tar.gz');
      setShowArchiveNotSupportedError(isArchive);
    } else {
      setShowArchiveNotSupportedError(false);
    }
  };

  const handleSupportsMobileUpdate = (supports: boolean) => {
    const isAndroidFriendly = supports;
    // unity games by default are iOS disabled if they support mobile, QA team will make sure they work fine on iOS
    const isIOSFriendly = !!(supports && gameLoaderType && !UNITY_GAME_LOADERS.includes(gameLoaderType));
    updateBaseForm({ ...baseForm, isAndroidFriendly, isIOSFriendly, orientation: supports ? 'BOTH' : null });
    setShowUnityiOSWarning(!!(gameLoaderType && UNITY_GAME_LOADERS.includes(gameLoaderType) && supports));
  };

  return (
    <>
      <UploadInstructions open={instructionDialogOpen} onClose={() => setInstructionDialogOpen(false)} />

      <InputTable>
        <TableBody>
          {renderLoaderTypeSelect()}
          {gameLoaderType === 'iframe' && renderIframeLinkInput()}
          {gameLoaderType && gameLoaderType !== 'iframe' && renderGameFilesUpload()}
        </TableBody>
      </InputTable>

      <Box>
        <Box>
          <GameDataSaveOptions
            value={progressSaveType}
            onChange={(progressSaveType) => updateBaseForm({ ...baseForm, progressSaveType })}
          />
        </Box>

        <Box display={'flex'} flexDirection={'column'} sx={{ mt: 2 }}>
          <StyledBodyText variant="h3" sx={{ mb: 1.25 }}>
            Game options
          </StyledBodyText>
          <Box display={'flex'} flexDirection={'column'} sx={{ pl: 1 }}>
            {/* to simplify things for the devs we display a single "Supports mobile" checkbox, which automatically checks both android and ios friendly. QA team will check and decide if iOS friendly should stay. */}
            <FormControlLabel
              label="The game supports mobile devices"
              checked={isAndroidFriendly || isIOSFriendly}
              control={<Checkbox onChange={(e) => handleSupportsMobileUpdate(e.currentTarget.checked)} />}
            />

            {showUnityiOSWarning && (
              <TextAlert>
                Unity games are disabled by default on iOS. Your game will be tested and enabled based on engagement stats.
              </TextAlert>
            )}

            <FormControlLabel
              label={
                <>
                  The game is an online{' '}
                  <a href={`${DOCS_URL}/requirements/multiplayer/`} target="_blank" rel="noreferrer">
                    multiplayer
                  </a>{' '}
                  game
                </>
              }
              checked={multiplayerOptions?.isMultiplayer}
              disabled={disableDetailsEditing} // we don't currently allow modifying multiplayer stuff for released games, this may change once multiplayer flags are moved to game
              control={
                <Checkbox
                  onChange={(e) => updateBaseForm({ ...baseForm, multiplayerOptions: { isMultiplayer: e.currentTarget.checked } })}
                />
              }
            />
            {!baseForm.gameJamId && (
              <FormControlLabel
                label="The game has in-game purchases"
                checked={hasIAP}
                control={<Checkbox onChange={(e) => updateBaseForm({ ...baseForm, hasIAP: e.currentTarget.checked })} />}
              />
            )}

            {hasIAP && (
              <TextAlert>
                In-game purchases are only allowed for selected games and should use CrazyGames' Xsolla account/integration. Contact{' '}
                <a
                  href="mailto:developer-relations@crazygames.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COLORS.warning[100], textDecoration: 'underline' }}
                >
                  developer-relations@crazygames.com
                </a>{' '}
                for approval.
              </TextAlert>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default UploadGameFilesForm;
